<template>
  <div class="container-fluid">
    <h1 class="mb-3">Device Management</h1>
    
    <div v-if="auth.isAdmin" class="row mb-3 align-items-center">
      <AddActions view="Device" @new="newModal"/>
      <div v-if="auth.isAdmin" class="col d-flex justify-content-end">
        <ToggleLockedButton 
          v-model="includeLocked" 
          class="me-2"
          @toggle="toggleLockedFilter"
        />
      </div>
    </div>
    <div>
      <DeviceForm v-if="modalNew || modalEdit || modalDetail"
        :tag="`device`" :msg="`${modalMsg} Device`"
        :schema="entitySchema" :schemaTypes="schemaTypes" :parentObj="editDevice"
        @formDataSubmitted="handleSave" @formClosed="closedModal"
      />
    </div>
    <DeviceTable 
      modelName="Device"
      :columns="columns" 
      :options="tableOptions"
      :ajaxUrl="deviceEndpoint" 
      :queryParams="deviceQueryParams" 
      ref="table"
    >
      <template #details="row">
        <TableActions mode="details" :rowData="row.rowData" @detail="detail" />
      </template>
      <template #actions="row">
        <TableActions mode="actions" :rowData="row.rowData" @edit="edit" @remove="remove" />
      </template>
    </DeviceTable>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useSchemaStore } from '@/stores/schemaStore';
import { ApiEndpoint } from '@/utils/config';
import { getCommonColumns, renderTime } from '@/utils/table';
import { handleRowSave } from '@/utils/form';

import DeviceTable from '@/components/TableComponent.vue';
import DeviceForm from '@/components/DataFormModal.vue';
import TableActions from '@/components/TableActions.vue';
import AddActions from '@/components/AddActions.vue';
import ToggleLockedButton from '@/components/ToggleLockedButton.vue';

const auth = useAuthStore();
const schema = useSchemaStore();

const table = ref();
const deviceList = ref([]);
const entitySchema = ref([]);
const schemaTypes = reactive({});
const includeLocked = ref(false);

const modalNew = ref(false);
const modalEdit = ref(false);
const modalDetail = ref(false);
const editDevice = reactive({});

const modalMsg = computed(() => {
  if (modalNew.value) return 'Add';
  if (modalEdit.value) return 'Edit';
  return 'View';
});

const columns = [
  { title: 'Customer ID', data: 'customerId', width: '10%' },
  { title: 'Device ID', data: 'id', width: '15%', className: 'td-truncate' },
  { title: 'Description', data: 'description', className: 'td-truncate' },
  { title: 'IMSI', data: 'imsi', width: '10%' },
  { title: 'SIM APN', data: 'parentApnId', tooltip: 'APN configured on SIM/UE' },
  { title: 'API APN', data: 'apnId', tooltip: '(Virtual) APN used for NIDD API' },
  { title: 'UE State', data: 'state', tooltip: 'Only available for NIDD devices' },
  { title: 'State Time', data: 'stateTime', className: 'td-truncate', render: renderTime },

  ...getCommonColumns(auth.isAdmin),
];

const tableOptions = computed(() => {
  return {};
});

const deviceEndpoint = ApiEndpoint.DEVICES;
const deviceQueryParams = computed(() => {
  const params = {
    includeLocked: includeLocked.value ? includeLocked.value : undefined,
  };
  return params;
});

const loadDevices = async (forceRefresh = false) => {
  await nextTick();
  // Force DataTables to read parent boundaries and execute its first clean server network fetch
  if (table.value) {
    await table.value.reloadTable(forceRefresh);
  }
};

const toggleLockedFilter = async () => {
  await loadDevices(true);
};

const handleSave = async (detail) => {
  try {
    await handleRowSave('device', detail, { ...editDevice });
    closedModal();
    await loadDevices(true);
  } catch (err) {
    // Error handled by utility confirm()
  }
};

const resetDevice = (obj) => {
  Object.keys(obj).forEach(key => delete obj[key]);
};

const newModal = () => {
  resetDevice(editDevice);
  modalNew.value = true;
};

const closedModal = () => {
  modalNew.value = modalEdit.value = modalDetail.value = false;
};

const edit = (row) => {
  Object.assign(editDevice, row);
  modalEdit.value = true;
};

const detail = (row) => {
  Object.assign(editDevice, row);
  modalDetail.value = true;
};

const remove = (row) => {
  console.warn(`Remove operation not supported for Devices`);
};

onMounted(async () => {
  entitySchema.value = schema.getFieldMeta('device');
  schemaTypes.value = schema.getModelEnums('device');  
});
</script>

<style scoped>
</style>
